import { TelnyxErrorCode } from "@redotech/redo-communication-primitives/text-messaging/telnyx-types";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { array, boolean, date, nativeEnum, object, string } from "zod";
import {
  ActionType,
  AiMerchantApproval,
  CommentState,
  ContentType,
  conversationMessageTextMessageType,
  ConversationMessageType,
  IntentType,
  MessageVisibility,
  suppressAutoRepliesValue,
} from "../conversation";
import { EmailReplyType } from "../support/conversations/email-info";
/**
 * Base schema for analytics interaction events
 * Contains common fields like productId, variantId, and timestamp
 */
export const BaseAnalyticsInteractionDbParser = z.object({
  productId: z.string(),
  variantId: z.string().nullish(),
  timestamp: z.date().nullish(),
});

/**
 * Schema for product interaction events (e.g., pdpInteractions)
 */
export const ProductInteractionDbParser =
  BaseAnalyticsInteractionDbParser.extend({
    action: z.string(),
    timestamp: z.date(), // Required for pdpInteractions
  });

/**
 * Schema for cart interaction events (e.g., productsAddedToCart)
 */
export const CartInteractionDbParser = BaseAnalyticsInteractionDbParser.extend({
  quantity: z.number(),
});

/**
 * Schema for back in stock subscription events
 */
export const BackInStockSubscriptionDbParser = BaseAnalyticsInteractionDbParser;

/**
 * Schema for suggested product events
 */
export const SuggestedProductDbParser = z.object({
  productId: z.string(),
  timestamp: z.date().nullish(),
});

export const facebookCommentDbParser = z.object({
  commentId: z.string().nullish(),
  postId: z.string().nullish(),
  facebookName: z.string().nullish(),
  pageScopedId: z.string().nullish(),
  hasBeenRepliedTo: z.boolean().nullish(),
  deleted: z.boolean().nullish(),
  state: nativeEnum(CommentState).nullish(),
  liked: z.boolean().nullish(),
});

export const instagramCommentDbParser = z.object({
  commentId: z.string().nullish(),
  mediaId: z.string().nullish(),
  username: z.string().nullish(),
  igScopedUserId: z.string().nullish(),
  hasBeenRepliedTo: z.boolean().nullish(),
  state: nativeEnum(CommentState).nullish(),
  liked: z.boolean().nullish(),
});

export const aiGenerationDbParser = z.object({
  text: z.string(),
  threadId: z.string().nullish(),
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
});

const FacebookMessageDbParser_ = z.object({
  messageId: z.string().nullish(),
  imageMessageIds: z.array(z.string()).nullish(),
  comment: facebookCommentDbParser.nullish(),
  privateRepliedComment: z
    .object({
      commentId: z.string().nullish(),
      commentText: z.string().nullish(),
      permalink: z.string().nullish(),
      redoMessageIdOfRepliedComment: zExt.objectId().nullish(),
      redoConversationIdOfRepliedComment: zExt.objectId().nullish(),
    })
    .nullish(),
});
export interface FacebookMessage extends z.infer<
  typeof FacebookMessageDbParser_
> {}
const FacebookMessageDbParser: z.ZodType<FacebookMessage> =
  FacebookMessageDbParser_;

const EmailMessageDbParser_ = z.object({
  textBody: z.string().nullish(),
  htmlBody: string().nullish(),
  messageId: z.string().nullish(),
  references: z.array(z.string()).nullish(),
  gmailMessageId: z.string().nullish(),
  outlookMessageId: z.string().nullish(),
  from: z
    .object({ email: z.string().nullish(), name: z.string().nullish() })
    .nullish(),
  to: array(
    object({ email: string().nullish(), name: string().nullish() }),
  ).optional(),
  cc: array(
    object({ email: string().nullish(), name: string().nullish() }),
  ).optional(),
  bcc: array(
    object({ email: string().nullish(), name: string().nullish() }),
  ).optional(),
  autoSubmitted: z.string().nullish(),
  suppressAutoReplies: z
    .array(
      createEnum(suppressAutoRepliesValue, {
        enumName: "SuppressAutoRepliesValue",
      }),
    )
    .nullish(),
});
const EmailMessageDbParser: z.ZodType<EmailMessage> = EmailMessageDbParser_;
export interface EmailMessage extends z.infer<typeof EmailMessageDbParser_> {}

const InstagramMessageDbParser_ = z.object({
  messageId: z.string().nullish(),
  imageMessageIds: z.array(z.string()).nullish(),
  repliedStoryUrl: z.string().nullish(),
  mentionedStoryUrl: z.string().nullish(),
  comment: instagramCommentDbParser.nullish(),
  privateRepliedComment: z
    .object({
      commentId: z.string().nullish(),
      commentText: z.string().nullish(),
      permalink: z.string().nullish(),
      redoMessageIdOfRepliedComment: zExt.objectId().nullish(),
      redoConversationIdOfRepliedComment: zExt.objectId().nullish(),
    })
    .nullish(),
});
export interface InstagramMessage extends z.infer<
  typeof InstagramMessageDbParser_
> {}
const InstagramMessageDbParser: z.ZodType<InstagramMessage> =
  InstagramMessageDbParser_;

const TextMessageDbParser_ = z.object({
  messageId: z.string().nullish(),
  kind: createEnum(conversationMessageTextMessageType, {
    enumName: "ConversationMessageTextMessageType",
  }).nullish(),
  providerErrors: z.array(nativeEnum(TelnyxErrorCode)).nullish(),
});
export interface TextMessage extends z.infer<typeof TextMessageDbParser_> {}
const TextMessageDbParser: z.ZodType<TextMessage> = TextMessageDbParser_;

const TiktokMessageDbParser_ = z.object({
  messageIds: z.array(z.string()).optional(),
});
export interface TiktokMessage extends z.infer<typeof TiktokMessageDbParser_> {}
const TiktokMessageDbParser: z.ZodType<TiktokMessage> = TiktokMessageDbParser_;

export const BaseMessageDbParser = object({
  _id: zExt.objectId().nullish(),
  aiMerchantApproval: createEnum(AiMerchantApproval, {
    enumName: "AiMerchantApproval",
  }).nullish(),
  aiMerchantApprovalReasoning: z.string().nullish(),
  aiMerchantFeedbackUpdatedAt: z.date().nullish(),
  aiGenerations: z.array(aiGenerationDbParser).nullish(),
  customer: zExt.objectId().nullish(),
  usersMentioned: array(zExt.objectId()).nullish(),
  user: zExt.objectId().nullish(),
  type: nativeEnum(ConversationMessageType),
  callRecord: zExt.objectId().nullish(),
  content: string(),
  sentAt: date(),
  visibility: nativeEnum(MessageVisibility).nullish(),
  contentType: nativeEnum(ContentType).nullish(),
  systemPromptId: string().nullish(),
  toolCallId: string().nullish(),
  actionType: nativeEnum(ActionType).nullish(),
  intent: nativeEnum(IntentType).nullish(),
  draftInfo: z
    .object({
      emailDraftInfo: z
        .object({
          inReplyTo: z.string().nullish(),
          replyType: nativeEnum(EmailReplyType),
          draftHtmlBody: string().nullish(),
        })
        .nullish(),
      isDraft: boolean().nullish(),
      inReplyToMongoId: z.string().nullish(),
    })
    .nullish(),
  files: z
    .array(
      z.object({
        url: z.string(),
        description: z.string().nullish(),
        mimeType: z.string().nullish(),
        originalContentId: z.string().nullish(),
        createdAt: z.date(),
        updatedAt: z.date().nullish(),
      }),
    )
    .nullish(),
  email: EmailMessageDbParser.nullish(),
  chatFlow: z.object({ htmlContent: string().nullish() }).nullish(),
  facebook: FacebookMessageDbParser.nullish(),
  instagram: InstagramMessageDbParser.nullish(),
  textMessage: TextMessageDbParser.nullish(),
  tiktok: TiktokMessageDbParser.nullish(),
  error: z.string().nullish(),
  externalOrigin: z.boolean().nullish(),
  orderList: z.boolean().nullish(),
  pendingToolCallId: z.string().nullish(),
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
  cancelledByUser: z.boolean().nullish(),
});
